import React from "react";
import { X, Trash2, FileDown, CreditCard } from "lucide-react";
import type { CartItem } from "../types";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface CartDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  items: CartItem[];
  onRemoveItem: (id: string) => void;
  onUpdateQuantity: (id: string, delta: number) => void;
}

export function CartDrawer({
  isOpen,
  onClose,
  items,
  onRemoveItem,
  onUpdateQuantity,
}: CartDrawerProps) {
  const [deliveryOption, setDeliveryOption] = React.useState<"pickup" | "cod">(
    () => {
      return (
        (localStorage.getItem("cart_deliveryOption") as "pickup" | "cod") ||
        "cod"
      );
    }
  );

  const subtotal = items.reduce(
    (sum, item) => sum + item.price * item.quantity,
    0
  );
  const codFee = deliveryOption === "cod" ? 400 : 0;
  const total = subtotal + codFee;

  const [form, setForm] = React.useState(() => {
    const saved = localStorage.getItem("cart_form");
    return saved
      ? JSON.parse(saved)
      : {
          firstName: "",
          lastName: "",
          mobile: "",
          address: "",
          city: "",
        };
  });

  const [validationErrors, setValidationErrors] = React.useState<{
    firstName?: boolean;
    mobile?: boolean;
    address?: boolean;
    city?: boolean;
  }>({});

  React.useEffect(() => {
    localStorage.setItem("cart_deliveryOption", deliveryOption);
    localStorage.setItem("cart_form", JSON.stringify(form));
  }, [deliveryOption, form]);

  const generatePDF = async () => {
    // Validation
    const errors: typeof validationErrors = {};

    if (!form.firstName.trim()) {
      errors.firstName = true;
    }
    if (!form.mobile.trim() || form.mobile.length !== 10) {
      errors.mobile = true;
    }
    if (deliveryOption === "cod") {
      if (!form.address.trim()) {
        errors.address = true;
      }
      if (!form.city.trim()) {
        errors.city = true;
      }
    }

    setValidationErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    const doc = new jsPDF();

    // Header Background
    doc.setFillColor(10, 10, 10);
    doc.rect(0, 0, 210, 40, "F");

    try {
      // Load Logo
      const logoUrl = "/images/sansi-logo.png";
      const img = new Image();
      img.src = logoUrl;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      // Calculate dimensions to fit in header (height: 40mm)
      const maxHeight = 30; // Leave 5mm padding top/bottom
      const ratio = img.width / img.height;
      const width = maxHeight * ratio;

      // Position logo on the left
      // Header is 210mm wide
      // Let's place it at x=15, centered vertically (y=5)
      doc.addImage(img, "PNG", 15, 5, width, maxHeight);

      // Adjust Title to be aligned with logo or kept centered
      // Keeping it centered for now
      doc.setTextColor(255, 140, 0); // Orange
      doc.setFontSize(22);
      doc.text("Sansi Eco Foods", 105, 25, { align: "center" });
    } catch (err) {
      console.error("Error loading logo for PDF:", err);
      // Fallback if logo fails
      doc.setTextColor(255, 140, 0); // Orange
      doc.setFontSize(22);
      doc.text("Sansi Eco Foods", 105, 25, { align: "center" });
    }

    doc.setTextColor(0, 0, 0);
    doc.setFontSize(16);
    doc.text("Quotation", 14, 50);

    doc.setFontSize(10);
    doc.text(`Date: ${new Date().toLocaleDateString()}`, 14, 58);

    // Customer Details
    // Customer Details
    // Customer Details
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Customer Information", 14, 70);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(`Name: ${form.firstName} ${form.lastName}`, 14, 77);
    doc.text(`Mobile: ${form.mobile}`, 14, 82);

    // Delivery Details
    doc.setFontSize(12);
    doc.setFont("helvetica", "bold");
    doc.text("Delivery Details", 110, 70);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(10);
    doc.text(
      `Method: ${deliveryOption === "pickup" ? "Store Pickup" : "Cash on Delivery"}`,
      110,
      77
    );

    let yPos = 82;
    if (deliveryOption === "cod") {
      doc.text(`Address: ${form.address}`, 110, yPos);
      doc.text(`City: ${form.city}`, 110, yPos + 5);
    }

    // Reset Y for table
    yPos = 100;

    const tableData = items.map((item) => [
      item.weight ? `${item.name} (${item.weight})` : item.name,
      item.quantity,
      `Rs. ${item.price.toLocaleString()}`,
      `Rs. ${(item.price * item.quantity).toLocaleString()}`,
    ]);

    const tableFoot = [];
    if (deliveryOption === "cod") {
      tableFoot.push(["", "", "Subtotal", `Rs. ${subtotal.toLocaleString()}`]);
      tableFoot.push(["", "", "COD Fee", `Rs. ${codFee.toLocaleString()}`]);
    }
    tableFoot.push(["", "", "Grand Total", `Rs. ${total.toLocaleString()}`]);

    autoTable(doc, {
      startY: yPos + 10,
      head: [["Product", "Qty", "Unit Price", "Total"]],
      body: tableData,
      foot: tableFoot,
      theme: "grid",
      headStyles: { fillColor: [74, 222, 128], textColor: [0, 0, 0] }, // Green
      footStyles: { fillColor: [255, 140, 0], textColor: [255, 255, 255] }, // Orange
    });

    // Footer Message
    const pageHeight = doc.internal.pageSize.height;
    doc.setFontSize(9);
    doc.setTextColor(128, 128, 128); // Gray color
    doc.text(
      `This quotation was auto-generated from sansiecofoods.lk on ${new Date().toLocaleString()}`,
      105,
      pageHeight - 10,
      { align: "center" }
    );

    doc.save("sansi-quotation.pdf");
  };

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 transition-opacity"
          onClick={onClose}
        />
      )}

      {/* Drawer */}
      <div
        className={`fixed top-0 right-0 h-full w-full max-w-md bg-[#1a1a1a] border-l border-white/10 z-50 transform transition-transform duration-300 ease-in-out ${isOpen ? "translate-x-0" : "translate-x-full"}`}
      >
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="p-6 border-b border-white/10 flex items-center justify-between">
            <h2 className="text-2xl font-bold text-white">Your Cart</h2>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/10 text-gray-400 hover:text-white"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Items */}
          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {items.length === 0 ? (
              <div className="text-center text-gray-500 mt-10">
                <p>Your cart is empty.</p>
              </div>
            ) : (
              <div className="space-y-6">
                <div className="space-y-4">
                  {items.map((item) => (
                    <div
                      key={item.id}
                      className="bg-white/5 p-4 flex gap-4 items-center"
                    >
                      <img
                        src={item.image}
                        alt={item.name}
                        className="w-16 h-16 object-contain bg-black/20"
                      />
                      <div className="flex-1">
                        <div className="font-bold text-white text-lg">
                          {item.name}
                        </div>
                        <p className="text-sansi-green">
                          Rs. {item.price.toLocaleString()}
                        </p>
                        <div className="flex items-center gap-3 mt-2">
                          <button
                            onClick={() => onUpdateQuantity(item.id, -1)}
                            className="w-6 h-6 bg-white/10 flex items-center justify-center hover:bg-white/20"
                          >
                            -
                          </button>
                          <span className="text-sm">{item.quantity}</span>
                          <button
                            onClick={() => onUpdateQuantity(item.id, 1)}
                            className="w-6 h-6 bg-white/10 flex items-center justify-center hover:bg-white/20"
                          >
                            +
                          </button>
                        </div>
                      </div>
                      <button
                        onClick={() => onRemoveItem(item.id)}
                        className="p-2 text-red-400 hover:bg-red-400/10"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Delivery Options & Form */}
                <div className="border-t border-white/10 pt-6 space-y-4 text-white">
                  <h3 className="font-semibold text-lg">Delivery Method</h3>
                  <div className="flex gap-4">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery"
                        value="pickup"
                        checked={deliveryOption === "pickup"}
                        onChange={(e) =>
                          setDeliveryOption(e.target.value as "pickup" | "cod")
                        }
                        className="accent-sansi-orange"
                      />
                      <span>Store Pickup</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="delivery"
                        value="cod"
                        checked={deliveryOption === "cod"}
                        onChange={(e) =>
                          setDeliveryOption(e.target.value as "pickup" | "cod")
                        }
                        className="accent-sansi-orange"
                      />
                      <span>Cash on Delivery</span>
                    </label>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <input
                        type="text"
                        placeholder="First Name *"
                        value={form.firstName}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            firstName: e.target.value.replace(
                              /[^a-zA-Z\s]/g,
                              ""
                            ),
                          });
                          if (validationErrors.firstName)
                            setValidationErrors({
                              ...validationErrors,
                              firstName: false,
                            });
                        }}
                        className={`w-full bg-white/10 border p-3 text-sm focus:outline-none ${validationErrors.firstName ? "border-red-500" : "border-white/20 focus:border-sansi-orange"}`}
                      />
                      {validationErrors.firstName && (
                        <p className="text-red-500 text-xs mt-1">Required</p>
                      )}
                    </div>
                    <div>
                      <input
                        type="text"
                        placeholder="Last Name"
                        value={form.lastName}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            lastName: e.target.value.replace(
                              /[^a-zA-Z\s]/g,
                              ""
                            ),
                          })
                        }
                        className="w-full bg-white/10 border border-white/20 p-3 text-sm focus:outline-none focus:border-sansi-orange"
                      />
                    </div>

                    {deliveryOption === "cod" && (
                      <>
                        <div className="col-span-2">
                          <input
                            type="text"
                            placeholder="Address *"
                            value={form.address}
                            onChange={(e) => {
                              setForm({ ...form, address: e.target.value });
                              if (validationErrors.address)
                                setValidationErrors({
                                  ...validationErrors,
                                  address: false,
                                });
                            }}
                            className={`w-full bg-white/10 border p-3 text-sm focus:outline-none ${validationErrors.address ? "border-red-500" : "border-white/20 focus:border-sansi-orange"}`}
                          />
                          {validationErrors.address && (
                            <p className="text-red-500 text-xs mt-1">
                              Required
                            </p>
                          )}
                        </div>
                        <div>
                          <input
                            type="text"
                            placeholder="City *"
                            value={form.city}
                            onChange={(e) => {
                              setForm({ ...form, city: e.target.value });
                              if (validationErrors.city)
                                setValidationErrors({
                                  ...validationErrors,
                                  city: false,
                                });
                            }}
                            className={`w-full bg-white/10 border p-3 text-sm focus:outline-none ${validationErrors.city ? "border-red-500" : "border-white/20 focus:border-sansi-orange"}`}
                          />
                          {validationErrors.city && (
                            <p className="text-red-500 text-xs mt-1">
                              Required
                            </p>
                          )}
                        </div>
                      </>
                    )}

                    <div
                      className={deliveryOption === "cod" ? "" : "col-span-2"}
                    >
                      <input
                        type="tel"
                        placeholder="Mobile Number *"
                        value={form.mobile}
                        maxLength={10}
                        onChange={(e) => {
                          setForm({
                            ...form,
                            mobile: e.target.value.replace(/[^0-9]/g, ""),
                          });
                          if (validationErrors.mobile)
                            setValidationErrors({
                              ...validationErrors,
                              mobile: false,
                            });
                        }}
                        className={`w-full bg-white/10 border p-3 text-sm focus:outline-none ${validationErrors.mobile ? "border-red-500" : "border-white/20 focus:border-sansi-orange"}`}
                      />
                      {validationErrors.mobile && (
                        <p className="text-red-500 text-xs mt-1">Required</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-6 border-t border-white/10 bg-black/20">
            <div className="mb-4 p-3 bg-yellow-500/10 border border-yellow-500/30">
              <p className="text-xs text-yellow-400/90 text-center flex items-center justify-center gap-2">
                <CreditCard className="w-4 h-4" />
                <span>
                  Online payments are not available yet, but coming soon!
                </span>
              </p>
            </div>
            <div className="space-y-2 mb-6">
              <div className="flex justify-between items-center text-gray-400">
                <span>Subtotal</span>
                <span>Rs. {subtotal.toLocaleString()}</span>
              </div>
              {deliveryOption === "cod" && (
                <div className="flex justify-between items-center text-gray-400">
                  <span>COD Fee</span>
                  <span>Rs. {codFee.toLocaleString()}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-4 border-t border-white/10">
                <span className="text-gray-400">Total</span>
                <span className="text-3xl font-bold text-sansi-orange">
                  Rs. {total.toLocaleString()}
                </span>
              </div>
            </div>

            <button
              onClick={generatePDF}
              disabled={items.length === 0}
              className="w-full bg-sansi-green hover:bg-white hover:text-black disabled:opacity-50 disabled:cursor-not-allowed text-black font-bold py-4 flex items-center justify-center gap-2 transition-all transform active:scale-95"
            >
              <FileDown className="w-5 h-5" />
              DOWNLOAD QUATATION
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
